/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  StopIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  LinkIcon,
  CubeIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import {
  pipelineService,
  PipelineRun,
  PipelineStepExecution,
  PipelineStepDef,
} from '../../services/registryApi';
import toast from 'react-hot-toast';

/** Parse JSON string fields defensively. */
const parseJson = <T,>(raw: any, fallback: T): T => {
  if (!raw) return fallback;
  if (typeof raw === 'object') return raw as T;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const formatDate = (date: string | null): string => {
  if (!date) return '-';
  return new Date(date).toLocaleString();
};

const formatDuration = (ms: number): string => {
  if (!ms) return '-';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

/* ── Status Badges ──────────────────────────────────────────────────── */
const RunStatusBadge: React.FC<{ status: PipelineRun['status']; large?: boolean }> = ({ status, large }) => {
  const cfg: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    running: 'bg-blue-500/20 text-blue-400 animate-pulse',
    completed: 'bg-green-500/20 text-green-400',
    failed: 'bg-red-500/20 text-red-400',
    cancelled: 'bg-gray-700 text-gray-400',
  };
  const sizeClass = large ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs';
  return (
    <span className={`inline-flex items-center font-medium rounded ${sizeClass} ${cfg[status] || cfg.pending}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const StepStatusBadge: React.FC<{ status: PipelineStepExecution['status'] }> = ({ status }) => {
  const cfg: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    running: 'bg-blue-500/20 text-blue-400 border-blue-500/30 animate-pulse',
    completed: 'bg-green-500/20 text-green-400 border-green-500/30',
    failed: 'bg-red-500/20 text-red-400 border-red-500/30',
    skipped: 'bg-gray-700 text-gray-400 border-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${cfg[status] || cfg.pending}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const StepTypeColor: Record<string, string> = {
  training: 'border-blue-500/40',
  evaluation: 'border-purple-500/40',
  deployment: 'border-green-500/40',
  custom: 'border-gray-600',
};

const StepStatusBorderColor: Record<string, string> = {
  pending: 'border-yellow-500/30',
  running: 'border-blue-500/50',
  completed: 'border-green-500/40',
  failed: 'border-red-500/40',
  skipped: 'border-gray-700',
};

/* ── Artifact Chip ──────────────────────────────────────────────────── */
const ArtifactChip: React.FC<{ label: string; value: string; icon: React.ElementType }> = ({
  label,
  value,
  icon: Icon,
}) => {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-800 text-gray-300 rounded border border-gray-700">
      <Icon className="w-3 h-3 text-gray-500" />
      <span className="text-gray-500">{label}:</span>
      <span className="font-mono">{value.length > 12 ? value.slice(0, 12) + '...' : value}</span>
    </span>
  );
};

/* ── DAG-style Step Visualization ───────────────────────────────────── */
const StepDag: React.FC<{
  steps: PipelineStepExecution[];
  stepsSnapshot: PipelineStepDef[];
}> = ({ steps, stepsSnapshot }) => {
  // Build a lookup for the snapshot definitions (to get depends_on)
  const defMap = useMemo(() => {
    const m = new Map<string, PipelineStepDef>();
    stepsSnapshot.forEach((s) => m.set(s.id, s));
    return m;
  }, [stepsSnapshot]);

  // Build execution lookup by step_id
  const execMap = useMemo(() => {
    const m = new Map<string, PipelineStepExecution>();
    steps.forEach((s) => m.set(s.step_id, s));
    return m;
  }, [steps]);

  // Order steps topologically using the snapshot defs (or fall back to execution list)
  const orderedStepIds = useMemo(() => {
    if (stepsSnapshot.length > 0) return stepsSnapshot.map((s) => s.id);
    return steps.map((s) => s.step_id);
  }, [stepsSnapshot, steps]);

  if (orderedStepIds.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <Cog6ToothIcon className="mx-auto h-10 w-10 text-gray-600 mb-2" />
        <p className="text-sm">No step executions found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {orderedStepIds.map((stepId, index) => {
        const def = defMap.get(stepId);
        const exec = execMap.get(stepId);
        const stepName = exec?.step_name || def?.name || stepId;
        const stepType = exec?.step_type || def?.type || 'custom';
        const status = exec?.status || 'pending';
        const dependsOn = def?.depends_on || [];

        return (
          <div key={stepId} className="flex items-start gap-3">
            {/* Connector */}
            <div className="flex flex-col items-center pt-1">
              <div
                className={`w-3 h-3 rounded-full border-2 ${
                  status === 'completed'
                    ? 'bg-green-500 border-green-400'
                    : status === 'running'
                    ? 'bg-blue-500 border-blue-400 animate-pulse'
                    : status === 'failed'
                    ? 'bg-red-500 border-red-400'
                    : status === 'skipped'
                    ? 'bg-gray-600 border-gray-500'
                    : 'bg-yellow-500 border-yellow-400'
                }`}
              />
              {index < orderedStepIds.length - 1 && (
                <div className="w-px flex-1 min-h-[2rem] bg-gray-700 mt-1" />
              )}
            </div>

            {/* Step card */}
            <div
              className={`flex-1 rounded-lg border bg-gray-900 p-4 ${
                StepStatusBorderColor[status] || 'border-gray-700'
              }`}
            >
              {/* Top row: name, type, status, duration */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white">{stepName}</span>
                  <span className="px-1.5 py-0.5 text-xs rounded bg-gray-800 text-gray-400 capitalize">
                    {stepType}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <StepStatusBadge status={status as PipelineStepExecution['status']} />
                  {exec && exec.duration_ms > 0 && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <ClockIcon className="w-3.5 h-3.5" />
                      {formatDuration(exec.duration_ms)}
                    </span>
                  )}
                </div>
              </div>

              {/* Dependencies */}
              {dependsOn.length > 0 && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                  <LinkIcon className="w-3 h-3" />
                  <span>Depends on:</span>
                  {dependsOn.map((depId) => {
                    const depDef = defMap.get(depId);
                    return (
                      <span key={depId} className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700/50">
                        {depDef?.name || depId}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Linked artifacts */}
              {exec && (exec.run_id || exec.model_version_id || exec.deployment_id) && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <ArtifactChip label="Run" value={exec.run_id} icon={ArrowPathIcon} />
                  <ArtifactChip label="Model" value={exec.model_version_id} icon={CubeIcon} />
                  <ArtifactChip label="Deploy" value={exec.deployment_id} icon={BoltIcon} />
                </div>
              )}

              {/* Error */}
              {exec && exec.error && (
                <div className="mt-3 flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  <ExclamationTriangleIcon className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <pre className="text-xs text-red-300 whitespace-pre-wrap break-words font-mono">
                    {exec.error}
                  </pre>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ── Main Component ─────────────────────────────────────────────────── */
const PipelineRunDetail: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch run
  const { data: runData, isLoading } = useQuery({
    queryKey: ['pipeline-run', runId],
    queryFn: () => pipelineService.getRun(runId!),
    enabled: Boolean(runId),
    select: (res) => res.data,
    refetchInterval: (query) => {
      const status = (query.state.data as { run?: PipelineRun; steps?: PipelineStepExecution[] } | undefined)?.run?.status;
      // Auto-refresh while the run is still active
      if (status === 'running' || status === 'pending') return 5000;
      return false;
    },
  });

  const run = runData?.run;
  const stepExecutions = runData?.steps || [];

  // Parse the steps_snapshot to get dependency info
  const stepsSnapshot = useMemo(() => {
    if (!run) return [];
    return parseJson<PipelineStepDef[]>(run.steps_snapshot, []);
  }, [run]);

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: () => pipelineService.cancelRun(runId!),
    onSuccess: () => {
      toast.success('Pipeline run cancelled');
      queryClient.invalidateQueries({ queryKey: ['pipeline-run', runId] });
    },
    onError: () => {
      toast.error('Failed to cancel pipeline run');
    },
  });

  const canCancel = run && (run.status === 'running' || run.status === 'pending');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-700 border-t-orange-500" />
      </div>
    );
  }

  if (!run) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Cog6ToothIcon className="mx-auto h-12 w-12 text-gray-600" />
          <p className="mt-3 text-gray-400">Pipeline run not found.</p>
          <Link
            to="/registry/pipelines"
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-orange-400 hover:text-orange-300"
          >
            <ArrowLeftIcon className="w-4 h-4" /> Back to pipelines
          </Link>
        </div>
      </div>
    );
  }

  // Compute duration display
  const durationDisplay = run.duration_ms
    ? formatDuration(run.duration_ms)
    : run.started_at
    ? 'In progress...'
    : '-';

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">Pipeline Run</h1>
              <RunStatusBadge status={run.status} large />
            </div>
            <p className="mt-1 text-sm text-gray-500 font-mono">{run.id}</p>
          </div>
          {canCancel && (
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to cancel this run?')) {
                  cancelMutation.mutate();
                }
              }}
              disabled={cancelMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <StopIcon className="w-4 h-4" />
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Run'}
            </button>
          )}
        </div>

        {/* Run Info */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Pipeline</p>
              <Link
                to={`/registry/pipelines/${run.pipeline_id}`}
                className="text-sm text-orange-400 hover:text-orange-300 transition-colors"
              >
                {run.pipeline_id.slice(0, 12)}...
              </Link>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Triggered By</p>
              <span className="flex items-center gap-1.5 text-sm text-gray-300">
                <UserIcon className="w-4 h-4 text-gray-500" />
                {run.triggered_by || '-'}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Trigger Reason</p>
              <span className="text-sm text-gray-300">
                {run.trigger_reason || '-'}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Started At</p>
              <span className="flex items-center gap-1.5 text-sm text-gray-300">
                <CalendarIcon className="w-4 h-4 text-gray-500" />
                {formatDate(run.started_at)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-4 pt-4 border-t border-gray-800">
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Duration</p>
              <span className="flex items-center gap-1.5 text-sm text-gray-300">
                <ClockIcon className="w-4 h-4 text-gray-500" />
                {durationDisplay}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Completed At</p>
              <span className="text-sm text-gray-300">
                {formatDate(run.completed_at)}
              </span>
            </div>
            {run.temporal_workflow_id && (
              <div className="sm:col-span-2">
                <p className="text-xs text-gray-500 uppercase font-medium mb-1">Workflow ID</p>
                <span className="text-sm text-gray-400 font-mono break-all">
                  {run.temporal_workflow_id}
                </span>
              </div>
            )}
          </div>

          {/* Run-level error */}
          {run.error && (
            <div className="mt-4 pt-4 border-t border-gray-800">
              <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-300 mb-1">Run Error</p>
                  <pre className="text-xs text-red-300/80 whitespace-pre-wrap break-words font-mono">
                    {run.error}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Step DAG Visualization */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            Step Execution ({stepExecutions.length})
          </h2>

          <StepDag steps={stepExecutions} stepsSnapshot={stepsSnapshot} />
        </div>
      </div>
    </div>
  );
};

export default PipelineRunDetail;
